import React, {useEffect, useRef} from 'react';
import {Animated, Easing, StyleSheet, View} from 'react-native';

const BASE = '#EDE8DB';

function usePulse() {
  const opacity = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.55,
          duration: 700,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return opacity;
}

function Block({
  width,
  height,
  radius = 8,
  opacity,
}: {
  width: number | string;
  height: number;
  radius?: number;
  opacity: Animated.Value;
}) {
  return (
    <Animated.View
      style={{
        width: width as any,
        height,
        borderRadius: radius,
        backgroundColor: BASE,
        opacity,
      }}
    />
  );
}

export function ChatSkeleton() {
  const opacity = usePulse();

  return (
    <View testID="club-chat-skeleton" style={styles.chat}>
      <View style={styles.chatRows}>
        <View style={styles.otherMessage}>
          <Block width={30} height={30} radius={15} opacity={opacity} />
          <View style={styles.messageLines}>
            <Block width={72} height={9} radius={5} opacity={opacity} />
            <Block width="72%" height={42} radius={12} opacity={opacity} />
          </View>
        </View>
        <View style={styles.myMessage}>
          <Block width="58%" height={38} radius={12} opacity={opacity} />
        </View>
        <View style={styles.otherMessage}>
          <Block width={30} height={30} radius={15} opacity={opacity} />
          <View style={styles.messageLines}>
            <Block width={92} height={9} radius={5} opacity={opacity} />
            <Block width="82%" height={58} radius={12} opacity={opacity} />
          </View>
        </View>
        <View style={styles.myMessage}>
          <Block width="44%" height={38} radius={12} opacity={opacity} />
        </View>
      </View>
      <View style={styles.composer}>
        <Block width={38} height={38} radius={19} opacity={opacity} />
        <View style={styles.flex}>
          <Block width="100%" height={40} radius={20} opacity={opacity} />
        </View>
        <Block width={38} height={38} radius={19} opacity={opacity} />
      </View>
    </View>
  );
}

export function LeaderboardSkeleton({
  variant = 'mobile',
}: {
  variant?: 'mobile' | 'rail';
}) {
  const opacity = usePulse();
  const isRail = variant === 'rail';

  return (
    <View
      testID="club-leaderboard-skeleton"
      style={[styles.rankList, !isRail && styles.mobileList]}>
      {Array.from({length: isRail ? 6 : 8}).map((_, index) => (
        <View key={index} style={styles.rankRow}>
          <Block width={22} height={12} radius={5} opacity={opacity} />
          <Block
            width={isRail ? 28 : 34}
            height={isRail ? 28 : 34}
            radius={17}
            opacity={opacity}
          />
          <View style={styles.rankName}>
            <Block
              width={`${64 - (index % 3) * 8}%`}
              height={11}
              radius={5}
              opacity={opacity}
            />
            <Block width="38%" height={8} radius={4} opacity={opacity} />
          </View>
          <Block width={34} height={12} radius={5} opacity={opacity} />
        </View>
      ))}
    </View>
  );
}

export function MembersSkeleton({
  variant = 'mobile',
}: {
  variant?: 'mobile' | 'rail';
}) {
  const opacity = usePulse();
  const isRail = variant === 'rail';

  return (
    <View
      testID="club-members-skeleton"
      style={!isRail ? styles.mobileList : undefined}>
      {Array.from({length: isRail ? 5 : 8}).map((_, index) => (
        <View key={index} style={styles.memberRow}>
          <Block
            width={isRail ? 30 : 38}
            height={isRail ? 30 : 38}
            radius={19}
            opacity={opacity}
          />
          <View style={styles.rankName}>
            <Block
              width={`${58 + (index % 2) * 12}%`}
              height={11}
              radius={5}
              opacity={opacity}
            />
            <Block width="34%" height={8} radius={4} opacity={opacity} />
          </View>
          <Block width={48} height={18} radius={6} opacity={opacity} />
        </View>
      ))}
    </View>
  );
}

export function ClubRoomSkeleton({isWide}: {isWide: boolean}) {
  const opacity = usePulse();

  return (
    <View testID="club-room-skeleton" style={styles.room}>
      <View style={[styles.header, isWide && styles.headerWide]}>
        <Block
          width={isWide ? 48 : 38}
          height={isWide ? 48 : 38}
          radius={12}
          opacity={opacity}
        />
        <View style={styles.headerText}>
          <Block width={isWide ? 210 : 150} height={18} opacity={opacity} />
          <Block width={86} height={10} radius={5} opacity={opacity} />
        </View>
        <Block width={82} height={38} radius={20} opacity={opacity} />
      </View>

      {isWide ? (
        <View style={styles.desktopBody}>
          <View style={styles.chatCard}>
            <ChatSkeleton />
          </View>
          <View style={styles.rail}>
            <View style={styles.railCard}>
              <View style={styles.cardHeading}>
                <Block width={92} height={12} radius={5} opacity={opacity} />
                <Block width={116} height={30} radius={8} opacity={opacity} />
              </View>
              <LeaderboardSkeleton variant="rail" />
            </View>
            <View style={styles.railCard}>
              <Block width={110} height={12} radius={5} opacity={opacity} />
              <MembersSkeleton variant="rail" />
            </View>
          </View>
        </View>
      ) : (
        <>
          <View style={styles.tabs}>
            <Block width={48} height={14} radius={6} opacity={opacity} />
            <Block width={88} height={14} radius={6} opacity={opacity} />
            <Block width={68} height={14} radius={6} opacity={opacity} />
          </View>
          <ChatSkeleton />
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  flex: {flex: 1},
  room: {flex: 1, backgroundColor: '#FAF6EB'},
  header: {
    minHeight: 68,
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.08)',
  },
  headerWide: {paddingHorizontal: 28, paddingVertical: 18},
  headerText: {flex: 1, gap: 8},
  desktopBody: {
    flex: 1,
    minHeight: 0,
    flexDirection: 'row',
    gap: 20,
    paddingHorizontal: 28,
    paddingTop: 16,
  },
  chatCard: {
    flex: 2,
    minHeight: 0,
    backgroundColor: '#fff',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
  },
  rail: {flex: 1, gap: 16},
  railCard: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 16,
    gap: 12,
  },
  cardHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tabs: {
    height: 50,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 22,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.08)',
  },
  chat: {flex: 1, minHeight: 260, backgroundColor: '#fff'},
  chatRows: {flex: 1, padding: 16, gap: 18},
  otherMessage: {flexDirection: 'row', alignItems: 'flex-start', gap: 9},
  messageLines: {flex: 1, gap: 7},
  myMessage: {alignItems: 'flex-end'},
  composer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38,35,34,0.08)',
  },
  rankList: {gap: 6},
  mobileList: {paddingHorizontal: 14, paddingTop: 8},
  rankRow: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingHorizontal: 9,
    paddingVertical: 7,
    backgroundColor: '#fff',
    borderRadius: 12,
  },
  rankName: {flex: 1, gap: 6},
  memberRow: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 7,
  },
});
